import { UserRoles } from "@/constant";
import { TMenuConfig } from "@/components/menu";

export const defaultRolePermissions: Record<string, string[]> = {
  [UserRoles.SERVICE_PROVIDER]: [
    "/",
    "/service-management",
    "/service-provider-management",
  ],
  [UserRoles.ADMIN]: [
    "/",
    "/user-management",
    "/roles-management",
    "/boat-management",
    "/berth-management",
    "/habor-management",
    "/user-feedbacks",
    "/user-logs",
    "/export-logs",
    "/delete-logs",
    "/about-us",
    "/terms-and-conditions",
    "/privacy-policy",
    "/accessibiliy",
    "/cookie-consent-settings",
    "/region-management",
    "/poi-management",
    "/club-management",
    "/club-members",
    "/club-requests",
    "/berth-category-management",
    "/booking-management",
    "/harbors/create",
    "/harbors/bulk-import",
    "/harbors/bulk-update",
    "/harbors/edit",
    "/harbors/details",
    "/harbors/view",
    "/addon-management",
    "/piers-management",
    "/community-management",
    "/community/details",
    "/club-management/:id",
    "/external-services",
    "/notification-management",
    "/club-registrations",
    "/routing-management",
    "/routing-management/details/:id",
    "/routing-management/version-details/:id",
    "/routing-management/version-sources/:id",
    "/organization-management",
    "/booking-dashboard",
    "/club-management/create",
    "/club-management/edit/:id",
    "/club-management/view",
    "/harbors/deletion-requests",
    "/harbors/requests",
    "/icons-management",
    "/kpi-management",
    "/legal-compliance",
    "/deployements",
    "/deployments/:id",
    "/subscription-management",
    "/settlements",
    "/settlements/:id",
    "/service-provider-management",
    "/dmo-management",
    "/template-layout-management",
    "/system-announcements",
    "/promo-management/create",
    "/theme-routes",
  ],
  [UserRoles.BOATER]: ["/", "/community-management", "/community/details"],
  [UserRoles.MARINA]: [
    "/",
    "/habor-management",
    "/harbors/details",
    "/harbors/view",
    "/harbors/edit",
    "/notification-management",
    "/booking-management",
    "/subscription-transactions",
    "/subscription-transactions/:id",
  ],
  [UserRoles.REGIONAL_OPERATOR]: [
    "/",
    "/habor-management",
    "/harbors/details",
    "/harbors/view",
    "/harbors/edit",
    "/community/details",
    "/community-management",
    "/notification-management",
    "/booking-management",
    "/service-provider-management",
    "/theme-routes",
    "/subscription-transactions",
    "/subscription-transactions/:id",
  ],
  [UserRoles.YACHT_CLUB_ADMIN]: [
    "/",
    "/club-management",
    "/club-members",
    "/club-requests",
    "/club-management/:id",
    "/habor-management",
    "/harbors/details",
    "/harbors/view",
    "/harbors/edit",
    "/community/details",
    "/community-management",
    "/notification-management",
    "/booking-management",
    "/club-management/edit/:id",
    "/club-management/view",
    "/service-provider-management",
  ],
  [UserRoles.SUB_ADMIN]: [
    "/",
    "/habor-management",
    "/harbors/details",
    "/harbors/view",
    "/notification-management",
    "/region-management",
    "/service-provider-management",
    "/theme-routes",
  ],
  [UserRoles.DMO]: [
    "/",
    "/habor-management",
    "/harbors/details",
    "/harbors/view",
    "/notification-management",
    "/region-management",
    "/service-provider-management",
    "/theme-routes",
  ],
  [UserRoles.INSPECTOR]: [
    "/",
    "/habor-management",
    "/harbors/details",
    "/harbors/view",
  ],
};

let dynamicRolePermissions: Record<string, string[]> = {
  ...defaultRolePermissions,
};

export const updateRolePermissions = (
  apiRolePermissions: Record<string, string[]>,
): void => {
  const processedPermissions: Record<string, string[]> = {};

  Object.entries(apiRolePermissions).forEach(([role, paths]) => {
    const uniquePaths = new Set(paths);

    uniquePaths.add("/");

    processedPermissions[role] = Array.from(uniquePaths);
  });

  dynamicRolePermissions = {
    ...defaultRolePermissions,
    ...processedPermissions,
  };
};

export const getRolePermissions = (): Record<string, string[]> => {
  return dynamicRolePermissions;
};

export function filterMenuByRoles(
  menu: TMenuConfig,
  roles: string[],
): TMenuConfig {
  const allowed = new Set<string>();
  const currentPermissions = getRolePermissions();

  roles.forEach((role) => {
    currentPermissions[role]?.forEach((path: string) => allowed.add(path));
  });

  return menu
    .map((item) => {
      if (item.children && item.children.length > 0) {
        const filteredChildren = item.children.filter(
          (child) => child.path && allowed.has(child.path),
        );
        if (filteredChildren.length > 0) {
          return { ...item, children: filteredChildren };
        }
        // If item has children but none are allowed, check if the parent path is allowed
        if (item.path && allowed.has(item.path)) {
          return { ...item, children: [] };
        }
        return null;
      }
      if (item.path && allowed.has(item.path)) return item;
      return null;
    })
    .filter(Boolean) as TMenuConfig;
}

export function isRouteAllowed(path: string, roles: string[]): boolean {
  const currentPermissions = getRolePermissions();

  if (
    currentPermissions["user_role"] &&
    currentPermissions["user_role"].includes(path)
  ) {
    return true;
  }

  if (path === "/roles-management") {
    const hasAccess = roles.some((role) => {
      const allowed = currentPermissions[role]?.includes(path);

      return allowed;
    });

    return hasAccess;
  }

  return roles.some((role) => currentPermissions[role]?.includes(path));
}

export function getFirstAllowedRoute(roles: string[]): string {
  const currentPermissions = getRolePermissions();

  for (const role of roles) {
    if (currentPermissions[role]?.includes("/")) {
      return "/";
    }
  }

  for (const role of roles) {
    if (currentPermissions[role]?.length) {
      return currentPermissions[role][0];
    }
  }

  return "/";
}

export const rolePermissions = getRolePermissions();
